import { Idol, ItemSize, Position, GridState } from 'types';
import { GRID_ROWS, GRID_COLS, ITEM_TYPES } from 'constants/index';
import { getTradeIdFromModId } from './tradeUtils';

/**
 * Safely get a property from an item type, with fallback
 */
export const safeGetItemTypeProperty = <T>(
  item: Idol | null, 
  property: keyof ItemSize, 
  defaultValue: T
): T => {
  if (!item) return defaultValue;
  if (!item.size) return defaultValue;
  return (item.size[property] !== undefined ? item.size[property] : defaultValue) as T;
};

// For accessing properties within item.size
export const safeGetItemSizeProperty = <T>(
  item: Idol | null, 
  property: keyof ItemSize, 
  defaultValue: T
): T => {
  if (!item) return defaultValue;
  if (!item.size) return defaultValue;
  return (item.size[property] !== undefined ? item.size[property] : defaultValue) as T;
};

// For accessing top-level properties of item
export const safeGetItemProperty = <T>(
  item: Idol | null, 
  property: keyof Idol, 
  defaultValue: T
): T => {
  if (!item) return defaultValue;
  return (item[property] !== undefined ? item[property] : defaultValue) as T;
};

/**
 * Fix an incomplete or broken idol item
 */
export const fixItem = (item: Partial<Idol> | null): Idol | null => {
  if (!item) return null;
  
  // Create a fixed copy
  const fixedItem = {...item} as Idol;
  
  // Fix missing type
  if (!fixedItem.size) {
    fixedItem.size = ITEM_TYPES.MINOR;
  }
  
  // Fix missing position if needed
  if (!fixedItem.position) {
    fixedItem.position = { row: 0, col: 0 };
  }
  
  // Fix missing stats if needed
  if (!fixedItem.stats) {
    fixedItem.stats = [];
  }
  
  return fixedItem;
};

/**
 * Map an item type key to its stats key
 */
export const mapItemTypeToStatsKey = (typeKey: string): string => {
  const mapping: Record<string, string> = {
    'SMALL': 'MINOR',
    // Add other mappings if needed
  };
  return mapping[typeKey] || typeKey;
};

/**
 * Group idols by their type name
 */
export const groupIdolsByType = (items: Idol[]): Record<string, Idol[]> => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return {};
  }

  return items.reduce((acc: Record<string, Idol[]>, item) => {
    if (!item) return acc;
    
    // Get type name, with defensive fallbacks
    let typeName = "Unknown";
    if (item.size && item.size.name) {
      typeName = item.size.name;
    } else if (item.name) {
      typeName = item.name;
    }
    
    if (!acc[typeName]) {
      acc[typeName] = [];
    }
    acc[typeName].push(item);
    return acc;
  }, {});
};

/**
 * Check if an item can be placed at the specified position
 */
export const canPlaceItem = (
  grid: GridState,
  row: number, 
  col: number, 
  item: Idol | null
): boolean => {
  if (!item || !item.size || !grid || row < 0 || col < 0) {
    return false;
  }
  
  const width = item.size.width;
  const height = item.size.height;
  
  // Check if position is within grid bounds
  if (row + height > GRID_ROWS || col + width > GRID_COLS) {
    return false;
  }
  
  // Check if all cells in the item's area are free and not disabled
  for (let i = 0; i < height; i++) {
    for (let j = 0; j < width; j++) {
      if (!grid[row + i] || !grid[row + i][col + j] ||
          grid[row + i][col + j].occupied || 
          grid[row + i][col + j].disabled) {
        return false;
      }
    }
  }
  
  return true;
};

/**
 * Calculate total stats from a collection of items
 */
export const calculateTotalStats = (items: Idol[]): Record<string, number> => {
  const totalStats: Record<string, number> = {};
  
  if (Array.isArray(items)) {
    items.forEach(item => {
      if (item && Array.isArray(item.stats)) {
        item.stats.forEach(stat => {
          if (stat && typeof stat.modId !== 'undefined') {
            if (!totalStats[stat.modId]) {
              totalStats[stat.modId] = 0;
            }
            totalStats[stat.modId] += stat.value || 0;
          }
        });
      }
    });
  }
  
  return totalStats;
};

/**
 * Aggregate stats by tradeId instead of modId
 * This combines modifiers with the same tradeId but different modIds (e.g., same mod on different idol sizes)
 */
export const aggregateStatsByTradeId = (
  stats: Record<string, number>,
  _placedItems: Idol[]
): { tradeId: string; value: number; modIds: number[] }[] => {
  const tradeIdMap = new Map<string, { value: number; modIds: number[] }>();
  
  Object.entries(stats).forEach(([modIdStr, value]) => {
    const modId = Number(modIdStr);
    const tradeId = getTradeIdFromModId(modId);
    
    if (tradeId) {
      if (!tradeIdMap.has(tradeId)) {
        tradeIdMap.set(tradeId, { value: 0, modIds: [] });
      }
      const entry = tradeIdMap.get(tradeId);
      if (entry) {
        entry.value += value;
        entry.modIds.push(modId);
      }
    } else {
      // If no tradeId found, use modId as fallback
      const fallbackKey = `modId_${modId}`;
      if (!tradeIdMap.has(fallbackKey)) {
        tradeIdMap.set(fallbackKey, { value: 0, modIds: [] });
      }
      const entry = tradeIdMap.get(fallbackKey);
      if (entry) {
        entry.value += value;
        entry.modIds.push(modId);
      }
    }
  });
  
  return Array.from(tradeIdMap.entries()).map(([tradeId, data]) => ({
    tradeId,
    value: data.value,
    modIds: data.modIds
  }));
};

/**
 * Initialize an empty grid with disabled cells marked
 */
export const initializeEmptyGrid = (disabledCells: [number, number][]): GridState => {
  const initialGrid: GridState = Array(GRID_ROWS).fill(null).map(() => 
    Array(GRID_COLS).fill(null).map(() => ({ occupied: false, disabled: false }))
  );
  
  disabledCells.forEach(([row, col]) => {
    if (row < GRID_ROWS && col < GRID_COLS) {
      initialGrid[row][col].disabled = true;
    }
  });
  
  return initialGrid;
};

/**
 * Update grid when placing an item
 */
export const updateGridWithPlacedItem = (
  grid: GridState, 
  item: Idol, 
  position: Position
): GridState => {
  const { row, col } = position;
  const width = item.size.width;
  const height = item.size.height;
  
  const newGrid = JSON.parse(JSON.stringify(grid)) as GridState;
  
  for (let i = 0; i < height; i++) {
    for (let j = 0; j < width; j++) {
      if (row + i < GRID_ROWS && col + j < GRID_COLS) {
        newGrid[row + i][col + j].occupied = true;
      }
    }
  }
  
  return newGrid;
};

/**
 * Update grid when removing an item
 */
export const updateGridWithRemovedItem = (
  grid: GridState, 
  item: Idol
): GridState => {
  if (!item.position) return grid;
  
  const { row, col } = item.position;
  const width = item.size.width;
  const height = item.size.height;
  
  const newGrid = JSON.parse(JSON.stringify(grid)) as GridState;
  
  for (let i = 0; i < height; i++) {
    for (let j = 0; j < width; j++) {
      if (row + i < GRID_ROWS && col + j < GRID_COLS) {
        newGrid[row + i][col + j].occupied = false;
      }
    }
  }
  
  return newGrid;
};